/**
 * Custom hook for streaming research job progress via SSE.
 *
 * Consumes the /api/v2/research/{jobId}/stream SSE endpoint
 * and provides real-time progress updates and incremental results.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { auth } from '../config/firebase';
import { getImpersonationHeaders } from '../services/impersonationHeaders';

const API_URL = import.meta.env['VITE_API_URL'] ||
  'https://us-central1-label-doctor-f255e.cloudfunctions.net/apiProxy';

export interface ResearchProgress {
  pct: number;
  message: string;
  drugs_analyzed: number;
  drugs_found: number;
  drugs_total: number;
  // Letter-corpus counters (Task 15). Populated by the backend SSE
  // ``progress`` payload only when ``job.corpus === 'fda_letters'``; the
  // drug-named counters above remain populated in parallel because the
  // MVP shares the underlying DB columns. Frontend reads ``job.corpus``
  // to decide which set of counters to display, so the rendering side
  // never has to disambiguate the corpus from the payload itself.
  letters_analyzed?: number;
  letters_found?: number;
  letters_total?: number;
}

export interface ResearchDrugResult {
  drug_name: string;
  application_number?: string;
  analysis: string;
  rank: number;
}

interface ResearchStreamState {
  progress: ResearchProgress | null;
  drugs: ResearchDrugResult[];
  summary: string | null;
  status: 'connecting' | 'running' | 'complete' | 'error' | 'cancelled';
  error: string | null;
}

export function useResearchStream(jobId: string | null) {
  const [state, setState] = useState<ResearchStreamState>({
    progress: null,
    drugs: [],
    summary: null,
    status: 'connecting',
    error: null,
  });

  const abortRef = useRef<AbortController | null>(null);

  const connect = useCallback(async () => {
    if (!jobId) return;

    // Clean up previous connection
    if (abortRef.current) {
      abortRef.current.abort();
    }

    const controller = new AbortController();
    abortRef.current = controller;

    setState({
      progress: null,
      drugs: [],
      summary: null,
      status: 'connecting',
      error: null,
    });

    try {
      // Get auth token
      const token = await auth.currentUser?.getIdToken();
      const headers: Record<string, string> = {
        'Accept': 'text/event-stream',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      Object.assign(headers, getImpersonationHeaders());

      const response = await fetch(
        `${API_URL}/v2/research/${jobId}/stream`,
        { headers, signal: controller.signal },
      );

      if (!response.ok) {
        throw new Error(`Stream failed: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';

        for (const part of parts) {
          if (!part.trim()) continue;

          let eventType = 'message';
          let eventData = '';

          for (const line of part.split('\n')) {
            if (line.startsWith('event: ')) {
              eventType = line.slice(7).trim();
            } else if (line.startsWith('data: ')) {
              eventData = line.slice(6);
            }
          }

          if (!eventData) continue;

          try {
            const parsed = JSON.parse(eventData);

            switch (eventType) {
              case 'progress':
                setState(prev => ({
                  ...prev,
                  status: 'running',
                  progress: parsed,
                }));
                break;

              case 'drugs':
                setState(prev => ({
                  ...prev,
                  drugs: Array.isArray(parsed) ? parsed : prev.drugs,
                }));
                break;

              case 'summary':
                setState(prev => ({
                  ...prev,
                  summary: typeof parsed === 'string' ? parsed : JSON.stringify(parsed),
                }));
                break;

              case 'done':
                setState(prev => ({
                  ...prev,
                  status: parsed.status || 'complete',
                }));
                return;
            }
          } catch {
            // Skip unparseable events
          }
        }
      }
    } catch (err: any) {
      if (err.name === 'AbortError') return;
      setState(prev => ({
        ...prev,
        status: 'error',
        error: err.message,
      }));
    }
  }, [jobId]);

  useEffect(() => {
    connect();
    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, [connect]);

  return state;
}
